/**
 * apps/command/src/ui/theme.ts — the dark command theme, and the light one.
 *
 * ============================================================================
 * LIGHT IS NOW THE DEFAULT (owner directive, 2026-08-18).
 *
 * §15.2 made dark the default and the reasoning below still holds for a wall
 * display in a dim operations room — which is why the theme survives instead of
 * being deleted. But the owner named samkad.uz as the house style, and the
 * console an official has open beside this one all day is a light one. Two
 * government systems for the same province that disagree about something as
 * basic as background colour make each look like a prototype of the other.
 * ============================================================================
 *
 * §15.2 rules that "light mode is not required for v1" and gives one palette:
 * the dark command theme. §15.1 asks for "a national situation room, not a
 * startup dashboard", and operators work on wall displays and in rooms with
 * projection, where a white full-screen frame is genuinely worse than a dark
 * one.
 *
 * The owner asked for a light mode (2026-08-17). It ships as an EXPLICIT,
 * REMEMBERED CHOICE and not as a default, and specifically it does NOT follow
 * `prefers-color-scheme`: a governor whose laptop is set to light must not have
 * the sanctioned situation-room look silently replaced on first sight of the
 * system. Somebody who wants light asks for it once, and it is remembered.
 *
 * ============================================================================
 * NO COLOUR VALUE APPEARS IN THIS FILE.
 * ============================================================================
 *
 * Both palettes live in `src/index.css` as CSS custom properties, next to a
 * comment naming §15.2's table — which is the only place a colour may be
 * written, because `npm run mbt:check` fails the build on a hex anywhere under
 * `src/**\/*.tsx?`. This module sets ONE attribute; the stylesheet does the rest.
 *
 * That is also why the toggle needs no re-render of anything but itself: every
 * component already asks for `bg-panel` or `text-ink-500`, and those resolve
 * through the variables.
 */
export const THEMES = ['dark', 'light'] as const;
export type Theme = (typeof THEMES)[number];

/** `localStorage`, not a cookie: no server has any reason to know. */
/**
 * The key is VERSIONED, and the version was bumped when the palette changed
 * (2026-08-18).
 *
 * The old key holds choices made against a different design — when dark was the
 * default and light was the alternate. Reading them back after the two swapped
 * meanings would hand somebody who once clicked «Qorongʻi rejim» a permanently
 * dark panel and no way to know why the redesign never arrived for them. A new
 * key is a clean start for everyone, and the cost is one preference nobody set
 * deliberately under the new design.
 */
const STORAGE_KEY = 'mbt.theme.v2';

/**
 * The stored choice, or `light`.
 *
 * Wrapped, because `localStorage` throws rather than returning null in a
 * hardened browser profile or a private window — and a panel that fails to
 * start because it could not read a colour preference would be an absurd way to
 * lose a situation room.
 */
export function storedTheme(): Theme {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/**
 * Applies a theme to the document.
 *
 * Three things, and the third is the subtle one:
 *
 *  1. `data-theme` on `<html>` — the selector both palettes hang off.
 *  2. `color-scheme`, so the browser's own furniture (scrollbars, form controls,
 *     the overscroll gutter) matches. Without it a light panel keeps dark
 *     scrollbars, which reads as a half-finished port.
 *  3. THE INLINE BACKGROUND ON `<html>` IS REMOVED FOR LIGHT. `index.html`
 *     carries `style="background-color: …"` so a wall display's first paint is
 *     never a white flash before the stylesheet lands — an inline style wins
 *     over the stylesheet, so light mode has to take it away rather than
 *     out-specify it. Dark keeps it, and therefore keeps its flash-free cold
 *     start; light accepts one brief dark frame, which is the right way round
 *     for this room.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  // `index.html` carries an inline background for a flash-free cold start. It
  // now paints the LIGHT canvas, so dark is the theme that has to take it away.
  if (theme === 'dark') root.style.removeProperty('background-color');
}

export function persistTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // A profile that refuses storage still gets the theme for this session.
    // Silently: there is nothing useful to tell an operator about it.
  }
}
